import { forwardRef, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Popover } from 'radix-ui';

export interface ComboboxOption {
  value: string;
  label: string;
  /**
   * Muted second line under `label`, for telling apart options whose labels
   * are identical (client feedback 2026-08-20: the attendee console's Event
   * filter listed three events all titled "Test").
   *
   * Shown in the OPEN list only, never in the closed trigger - the trigger has
   * one line, and the selected option's identity is already settled by then.
   */
  hint?: string;
}

/**
 * Input and panel surfaces.
 *
 * Every LIGHT value here is the literal this component already shipped
 * (`border-slate`, `bg-white`, `text-navy`, `placeholder:text-input-placeholder`),
 * so the Figma-locked member screens that use this primitive - HQ location on
 * the startup create flow (MYN-46) and Sector/Stage - render byte-identically.
 * Only `admin-dark:` overrides are added, and that variant emits no rule in
 * light mode.
 *
 * Needed because the admin events console reuses this primitive for the
 * attendee console's Event filter, and the console has a dark theme: without
 * these the searchable filter would render as white-on-dark beside the
 * theme-aware `Select`/`MultiSelect` on the same filter row. `MultiSelect`
 * carries the same pair for the same reason.
 */
const INPUT_SURFACE =
  'border-slate bg-white text-navy placeholder:text-input-placeholder disabled:border-teal/5 disabled:bg-teal/5 ' +
  'admin-dark:border-border-strong admin-dark:bg-surface-card admin-dark:text-text-primary ' +
  'admin-dark:disabled:border-border-subtle admin-dark:disabled:bg-surface-raised';

/** Popover panel. Dark mode uses the overlay step so the panel does not
 * disappear into the card behind it (same choice as `MultiSelect`). */
const PANEL_SURFACE =
  'border-slate bg-white admin-dark:border-border-strong admin-dark:bg-surface-overlay';

export interface ComboboxProps {
  label: string;
  /** Current search text (controlled - the parent drives the search query from this). */
  inputValue: string;
  onInputChange: (value: string) => void;
  /** Currently selected option's label, shown in the closed trigger. */
  selectedLabel?: string;
  options: ComboboxOption[];
  onSelect: (option: ComboboxOption) => void;
  loading?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
}

/**
 * Searchable single-select (mnr-standards: complex interactive widgets use a
 * Radix primitive). Built on Radix `Popover` (no new dependency) rather than
 * a native `<select>` - needed for City (MYN-46, up to ~1,757 options for one
 * state), where a plain dropdown is unusable. Country/State stay the `Select`
 * primitive - their option counts are small enough for a native list.
 */
export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(function Combobox(
  {
    label,
    inputValue,
    onInputChange,
    selectedLabel,
    options,
    onSelect,
    loading = false,
    disabled = false,
    error,
    placeholder,
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const errorId = useId();
  const listboxId = useId();
  const hasError = Boolean(error);

  // The closed field shows the selected label, so typing into it would append to that label. A
  // printable key opens the field (showing the search text, empty at first) before the character
  // lands; `onChange` below also strips the label when the character landed before that re-render,
  // so the typed text always starts a fresh search.
  function openOnTyping(event: KeyboardEvent<HTMLInputElement>) {
    const isPrintable = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (isPrintable && !open) setOpen(true);
  }

  return (
    <div className="flex flex-col gap-1">
      <Popover.Root open={open && !disabled} onOpenChange={setOpen}>
        <Popover.Anchor asChild>
          <div className="relative">
            <input
              ref={(node) => {
                inputRef.current = node;
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
              }}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-label={label}
              aria-invalid={hasError}
              aria-describedby={hasError ? errorId : undefined}
              disabled={disabled}
              value={open ? inputValue : (selectedLabel ?? inputValue)}
              placeholder={placeholder}
              onFocus={() => setOpen(true)}
              onKeyDown={openOnTyping}
              onChange={(event) => {
                // A key typed into the CLOSED field lands after the selected label it shows; that
                // text is the start of a fresh search, not an edit of the label.
                const typed = event.target.value;
                const search =
                  !open && selectedLabel && typed.startsWith(selectedLabel)
                    ? typed.slice(selectedLabel.length)
                    : typed;
                onInputChange(search);
                setOpen(true);
              }}
              className={`rounded-input text-input focus:border-green w-full border px-4 py-2 focus:outline-none disabled:cursor-not-allowed ${INPUT_SURFACE}`}
            />
          </div>
        </Popover.Anchor>
        <Popover.Portal>
          <Popover.Content
            id={listboxId}
            role="listbox"
            onOpenAutoFocus={(event) => event.preventDefault()}
            // A click on the field itself is not "outside": closing here would put the selected
            // label back under the cursor and the next keystroke would append to it.
            onInteractOutside={(event) => {
              const target = event.detail.originalEvent.target;
              if (target instanceof Node && inputRef.current?.contains(target)) event.preventDefault();
            }}
            className={`rounded-button z-50 max-h-60 w-(--radix-popover-trigger-width) overflow-auto border py-1 shadow-lg ${PANEL_SURFACE}`}
            sideOffset={4}
          >
            {loading && <p className="text-slate admin-dark:text-text-muted px-3 py-2 text-sm">Searching…</p>}
            {!loading && options.length === 0 && (
              <p className="text-slate admin-dark:text-text-muted px-3 py-2 text-sm">No matches</p>
            )}
            {!loading &&
              options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.label === selectedLabel}
                  className="text-navy admin-dark:text-text-primary hover:bg-input-disabled admin-dark:hover:bg-row-hover block w-full cursor-pointer px-3 py-2 text-left text-sm"
                  onClick={() => {
                    onSelect(option);
                    setOpen(false);
                  }}
                >
                  {option.label}
                  {option.hint && (
                    <span className="text-slate admin-dark:text-text-muted block text-xs">
                      {option.hint}
                    </span>
                  )}
                </button>
              ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {hasError && (
        <p id={errorId} role="alert" className="text-red admin-dark:text-danger pl-1 text-xs">
          {error}
        </p>
      )}
    </div>
  );
});
